import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/InstagramInteractionTaggedPostDetails.graphql';
import { Query, QueryinstagramInteractionTaggedPostDetailsArgs } from '../../__generated__/globalTypes';

export const useInstagramInteractionTaggedPostDetailsQuery = (
  options?: QueryHookOptions<Query, QueryinstagramInteractionTaggedPostDetailsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryinstagramInteractionTaggedPostDetailsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useInstagramInteractionTaggedPostDetailsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryinstagramInteractionTaggedPostDetailsArgs>
): QueryResult<Query, QueryinstagramInteractionTaggedPostDetailsArgs> & {
  getInstagramInteractionTaggedPostDetails: LazyQueryExecFunction<
    Query,
    QueryinstagramInteractionTaggedPostDetailsArgs
  >;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getInstagramInteractionTaggedPostDetails, result] = useLazyQuery<
    Query,
    QueryinstagramInteractionTaggedPostDetailsArgs
  >(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getInstagramInteractionTaggedPostDetails, ...result };
};

export const useInstagramInteractionTaggedPostDetailsPromiseQuery = () => {
  const client = useApolloClient();

  const getInstagramInteractionTaggedPostDetails = useCallback(
    (options?: Omit<QueryOptions<QueryinstagramInteractionTaggedPostDetailsArgs, Query>, 'query'>) =>
      client.query<Query, QueryinstagramInteractionTaggedPostDetailsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getInstagramInteractionTaggedPostDetails };
};
// Suspense Query Hooks
export const useInstagramInteractionTaggedPostDetailsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryinstagramInteractionTaggedPostDetailsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryinstagramInteractionTaggedPostDetailsArgs>(QUERY, {
    ...options
  });

  return result;
};
